/**
 * `@ronceray/contract` — the local save/open file format (US-016, FR-27, FR-30).
 *
 * This schema describes **what the patched HTML actually writes**, not an idealised format. It was
 * written by reading `sauver()` and `charger()` in `Calcul_structures_EC3_EC5.html` (the live file,
 * around lines 1195–1225), and every tolerance below cites the line of behaviour it mirrors. If the
 * two ever disagree, the HTML is right and this file is the bug: engineers already have saved files
 * on disk, and a contract that cannot read them is not a contract.
 *
 * What `sauver()` writes, verbatim:
 *
 * ```js
 * var data = { schemaVersion: 1, format: 'ronceray-calc-1', projet: <string>,
 *              onglet: <'lamine'|'creux'|'bois'>, cas: {} };
 * ['lamine','creux','bois'].forEach(function (m) { data.cas[m] = lire(m); });
 * ```
 *
 * ------------------------------------------------------------------------------------------------
 * NOTE FOR THE P2 IMPORTER: **ONE SAVED FILE YIELDS UP TO THREE IMPORTABLE CASES, NOT ONE.**
 * ------------------------------------------------------------------------------------------------
 *
 * The file is multi-module by construction: `sauver()` calls `lire()` for *all three* modules on
 * every save, regardless of which tab the user was looking at. A saved file is therefore a small
 * project — a laminated-section case, a hollow-section case and a timber case, sharing one `projet`
 * name — and an importer that creates a single record from it silently discards two thirds of the
 * user's work. `onglet` records which tab was in front; it is a UI hint, not a selector, and must
 * never be used to decide which case to keep. {@link savedFileToImportableCases} does the fan-out.
 *
 * @see ERRORS.md — repeats the three-cases note for readers who arrive at the documentation.
 */

import { z } from 'zod';

/* ================================================================================================
 * Modules
 * ============================================================================================== */

/**
 * The three calculation modules, in the order `sauver()` iterates them. That order is preserved
 * here so a normalised file and a fan-out of importable cases are byte-stable and diffable.
 */
export const SAVED_FILE_MODULES = ['lamine', 'creux', 'bois'] as const;

export const savedModuleSchema = z.enum(SAVED_FILE_MODULES);
export type SavedModule = z.infer<typeof savedModuleSchema>;

/**
 * Tab shown when a file is opened but `onglet` is missing (only possible in v0). Matches the live
 * file's own initial state: `var courant = 'lamine';`.
 */
export const DEFAULT_SAVED_MODULE: SavedModule = 'lamine';

/* ================================================================================================
 * The per-module case payload
 * ============================================================================================== */

/**
 * One value as `lire()` produces it.
 *
 * ```js
 * o[el.dataset.id] = el.type === 'number' ? parseFloat(el.value) : el.value;
 * ```
 *
 * So: `<input type="number">` gives a **number**, and `<select>` / `<input type="text">` give a
 * **string**. Two consequences that a hand-written schema always gets wrong:
 *
 *  - `classeService` is a `<select>`, so a saved timber case carries the **string `'2'`**, never the
 *    number `2`. The same goes for every other select-backed field (`profil`, `nuance`, `classe`,
 *    `section`, `fabrication`, `duree`, `situation`, `modeFleche`, `schema`) and for `repere`.
 *  - `parseFloat('')` is `NaN`, and `JSON.stringify(NaN)` is `null`. A saved file whose numeric
 *    field was left blank therefore contains a literal `null`. Rejecting it would reject a file the
 *    tool itself wrote, so `null` is accepted here and left for the request schema to refuse at
 *    calculation time, with a per-field message, where the user can actually see it.
 *
 * `boolean` is accepted defensively: no field produces one today, but `ecrire()` assigns any value
 * straight to `el.value`, so a future checkbox would round-trip through this format unannounced.
 */
export const savedCaseValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type SavedCaseValue = z.infer<typeof savedCaseValueSchema>;

/**
 * One module's form state: every `[data-id]` field of that module's form.
 *
 * **Deliberately a loose-but-typed record, not a per-field object.** Three reasons:
 *
 *  1. A saved file is a *snapshot of a form*, not a validated request. `lire()` sweeps the DOM and
 *     writes whatever is in each input, including blanks and half-finished edits. A strict schema
 *     here would reject files the tool itself wrote — which is the one thing a save format may
 *     never do.
 *  2. The field set is versioned by the HTML, not by this package. A field added to `FICHES` in the
 *     live file must not make every previously saved file unparseable.
 *  3. The strict, per-field, unit-annotated schemas are **US-013's job** (`./request`), and they
 *     apply at the point of calculation. Duplicating them here would put the same shape in two
 *     files, which is exactly the failure mode this package exists to prevent. The importer's job
 *     is: parse loosely with this schema, then validate each of the three cases with the matching
 *     request schema and report failures per field with the `422` mapper in `./errors`.
 */
export const savedCaseSchema = z.record(z.string(), savedCaseValueSchema);
export type SavedCase = z.infer<typeof savedCaseSchema>;

/* ================================================================================================
 * Version constants
 * ============================================================================================== */

/**
 * The `format` field's value — a **legacy constant**.
 *
 * It predates versioning, it is kept only so that older readers of the file are not surprised, and
 * it is **never used for version detection**. `sauver()` says so in its own comment, and
 * `charger()` never reads it. The schemas below accept the field, echo it back and assert nothing
 * about its value: asserting on it would turn a field documented as *not authoritative* into a
 * version signal, which is precisely the mistake the `schemaVersion` field exists to prevent.
 *
 * `schemaVersion` is the only authority. See {@link detectSavedFileVersion}.
 */
export const SAVED_FILE_FORMAT = 'ronceray-calc-1';

/** The current schema version, written by `sauver()` (FR-30). */
export const SAVED_FILE_SCHEMA_VERSION = 1;

/* ================================================================================================
 * v1 — what the patched HTML writes today
 * ============================================================================================== */

/**
 * Schema version 1: the multi-module shape written by the patched `sauver()` (FR-30).
 *
 * `cas` requires **all three** modules, because `sauver()` writes all three on every save without
 * exception. A v1 file missing a module was not written by this tool, and P2 should treat it as
 * corrupt rather than silently import a partial project. (`charger()` is more forgiving — it loops
 * and skips absent modules — but its forgiveness exists for v0 files, which is where this schema
 * puts it.)
 */
export const savedFileV1Schema = z.object({
  /** Authoritative. Required, and exactly `1` for this schema. */
  schemaVersion: z.literal(1),
  /**
   * Legacy constant (see {@link SAVED_FILE_FORMAT}). Optional and unasserted on input; defaulted on
   * output so consumers always see a value, and never load-bearing.
   */
  format: z.string().optional().default(SAVED_FILE_FORMAT),
  /** Project name, from the `#projet` input. May be the empty string — the input starts blank. */
  projet: z.string(),
  /** Tab in front at save time. A UI hint only — never a selector for which case to import. */
  onglet: savedModuleSchema,
  /** One entry per module, always all three. */
  cas: z.object({
    lamine: savedCaseSchema,
    creux: savedCaseSchema,
    bois: savedCaseSchema,
  }),
});
export type SavedFileV1 = z.infer<typeof savedFileV1Schema>;

/* ================================================================================================
 * v0 — version-less legacy files
 * ============================================================================================== */

/**
 * Schema version 0: **version-less legacy files**, i.e. internal files saved before FR-30 added
 * `schemaVersion`. There is no `schemaVersion: 0` on disk anywhere; v0 is *defined* as its absence,
 * exactly as `charger()` defines it:
 *
 * ```js
 * var version = (typeof d.schemaVersion === 'number') ? d.schemaVersion : 0;
 * ```
 *
 * The field correspondence is identical to v1 — `sauver()` was patched to add `schemaVersion`, not
 * to reorganise the payload — so the difference is tolerance, not structure. Every field is
 * optional here because `charger()` guards each one (`if (d.projet)`, `if (d.cas && d.cas[m])`,
 * `if (d.onglet)`) and reads whatever is present without complaint. `schemaVersion` is pinned to
 * `undefined` so that a file carrying *any* version number can never be silently mistaken for a v0
 * file by {@link savedFileSchema}.
 */
export const savedFileV0Schema = z.object({
  /** Must be absent. Its absence is what makes the file v0. */
  schemaVersion: z.undefined().optional(),
  format: z.string().optional().default(SAVED_FILE_FORMAT),
  projet: z.string().optional(),
  onglet: savedModuleSchema.optional(),
  /**
   * Partial: an old file may carry fewer than three modules, and `charger()` imports whichever are
   * present.
   */
  cas: z
    .object({
      lamine: savedCaseSchema.optional(),
      creux: savedCaseSchema.optional(),
      bois: savedCaseSchema.optional(),
    })
    .optional(),
});
export type SavedFileV0 = z.infer<typeof savedFileV0Schema>;

/**
 * Either supported version. v1 is tried first; a file with no `schemaVersion` falls through to v0,
 * and a file with an unrecognised `schemaVersion` matches neither (see {@link parseSavedFile} for
 * the version-aware entry point, which reports that case explicitly instead of as a union failure).
 */
export const savedFileSchema = z.union([savedFileV1Schema, savedFileV0Schema]);
export type SavedFile = z.infer<typeof savedFileSchema>;

/* ================================================================================================
 * Version detection, normalisation, and the fan-out to importable cases
 * ============================================================================================== */

/**
 * The version of an as-yet-unvalidated parsed JSON value, by the **same rule `charger()` uses**: a
 * numeric `schemaVersion` is the version; anything else — absent, `null`, a string, the file not
 * being an object at all — is v0.
 *
 * Note what is *not* consulted: `format`. See {@link SAVED_FILE_FORMAT}.
 *
 * Returns the raw number, which may be a version this build does not support (a file written by a
 * newer release). Callers must handle that; {@link parseSavedFile} does.
 */
export function detectSavedFileVersion(value: unknown): number {
  if (typeof value === 'object' && value !== null && 'schemaVersion' in value) {
    const raw = (value as { schemaVersion?: unknown }).schemaVersion;
    if (typeof raw === 'number') return raw;
  }
  return 0;
}

/** A saved file after normalisation: one shape for both versions, with the source version kept. */
export interface NormalisedSavedFile {
  /** `0` or `1` — which schema the file was read with. Keep it: it is provenance, not trivia. */
  readonly sourceVersion: 0 | 1;
  /** The legacy `format` string, echoed. Never branch on this. */
  readonly format: string;
  /** Project name; `''` when a v0 file carried none. */
  readonly projet: string;
  /** Tab hint; {@link DEFAULT_SAVED_MODULE} when a v0 file carried none. */
  readonly onglet: SavedModule;
  /** Partial, because a v0 file may hold fewer than three modules. v1 files always hold all three. */
  readonly cas: Partial<Record<SavedModule, SavedCase>>;
}

/** Collapse a parsed v0 or v1 file into the single {@link NormalisedSavedFile} shape. */
export function normaliseSavedFile(file: SavedFileV1 | SavedFileV0): NormalisedSavedFile {
  const cas: Partial<Record<SavedModule, SavedCase>> = {};
  for (const module of SAVED_FILE_MODULES) {
    const value = file.cas?.[module];
    if (value) cas[module] = value;
  }

  return {
    sourceVersion: file.schemaVersion === SAVED_FILE_SCHEMA_VERSION ? 1 : 0,
    format: file.format,
    projet: file.projet ?? '',
    onglet: file.onglet ?? DEFAULT_SAVED_MODULE,
    cas,
  };
}

/** Outcome of {@link parseSavedFile}. */
export type SavedFileParseResult =
  | { readonly ok: true; readonly version: 0 | 1; readonly file: NormalisedSavedFile }
  /** `schemaVersion` is a number this build does not know — a file from a newer release. */
  | { readonly ok: false; readonly reason: 'unsupported-version'; readonly version: number }
  /** The file is the right version but the wrong shape. `error.issues` names the offending path. */
  | {
      readonly ok: false;
      readonly reason: 'invalid';
      readonly version: 0 | 1;
      readonly error: z.core.$ZodError;
    };

/**
 * Read a parsed-JSON value as a saved file, accepting both v1 and version-less v0 files.
 *
 * This is the entry point the P2 importer should use: it detects the version the way `charger()`
 * does, validates with the matching schema, and normalises. It reports an unsupported future
 * version as its own outcome rather than as a shape error, because "this file is newer than this
 * build" and "this file is corrupt" call for different messages to the user.
 *
 * Remember that a successful parse yields **up to three** cases — call
 * {@link savedFileToImportableCases}.
 */
export function parseSavedFile(value: unknown): SavedFileParseResult {
  const version = detectSavedFileVersion(value);

  if (version === SAVED_FILE_SCHEMA_VERSION) {
    const parsed = savedFileV1Schema.safeParse(value);
    return parsed.success
      ? { ok: true, version: 1, file: normaliseSavedFile(parsed.data) }
      : { ok: false, reason: 'invalid', version: 1, error: parsed.error };
  }

  if (version === 0) {
    const parsed = savedFileV0Schema.safeParse(value);
    return parsed.success
      ? { ok: true, version: 0, file: normaliseSavedFile(parsed.data) }
      : { ok: false, reason: 'invalid', version: 0, error: parsed.error };
  }

  return { ok: false, reason: 'unsupported-version', version };
}

/** One module's case, lifted out of a saved file and ready to be validated and stored. */
export interface ImportableCase {
  readonly module: SavedModule;
  /** The file's project name, copied onto every case — all three belong to the same project. */
  readonly projet: string;
  /** The raw form snapshot. Validate it with the matching request schema from `./request`. */
  readonly input: SavedCase;
  /** `true` for the module named by `onglet`. A display hint; never a filter. */
  readonly active: boolean;
}

/**
 * Fan one saved file out into its cases — **up to three, never one**.
 *
 * A v1 file always yields exactly three (`sauver()` writes all three modules on every save). A v0
 * file yields however many it carried, in module order. This function is the executable form of the
 * P2 importer note at the top of this file: if the importer calls it, it cannot accidentally keep
 * only the `onglet` module.
 */
export function savedFileToImportableCases(file: NormalisedSavedFile): ImportableCase[] {
  const cases: ImportableCase[] = [];
  for (const module of SAVED_FILE_MODULES) {
    const input = file.cas[module];
    if (!input) continue;
    cases.push({ module, projet: file.projet, input, active: module === file.onglet });
  }
  return cases;
}
